import { styled } from "@mui/material";

import {
  MID,
  NOT_WIDE,
  SITE_STEPS,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";

/**
 * The scope for the CMS's rich-text HTML.
 *
 * The markup arrives as a string, so its elements cannot take MUI props:
 * element selectors are the only seam, and this is the one place the codebase
 * uses them.
 *
 * Every value is measured off the published articles rather than judged, and
 * the set of posts measured matters as much as the values: the elements below
 * appear on 94% (links), 89% (figures) and 73% (lists) of posts but on none of
 * the post this component was first written against.
 *
 * The rules the published page emits at element level are NOT here, they live
 * in `articleBaseline` in MarketingShell, because emotion prefixes this
 * component's generated class outside any `:where()`, which would rank them
 * above a post's own `<style>` and invert the cascade. See that file.
 */
const ArticleBody = styled("article")(({ theme }) => ({
  width: "100%",
  fontSize: 16,
  fontWeight: 400,
  // A ratio, not 24px: descendants at another size inherit it proportionally,
  // which is how the published article leads its 15px table cells at 22.5px.
  lineHeight: 1.5,
  color: theme.palette.text.primary,

  // No last-child reset. The published body keeps that margin: its rich text
  // nests each section in a div, and the trailing paragraph's 16px collapses
  // out of the div and into the body, which is a flex item and so contains it.
  // Ours is flat, so the same margin is the last child's own, zeroing it made
  // every article body on both surfaces exactly 16px short.
  "& p": { marginBlock: 0, marginBottom: 16 },

  "& h2": {
    ...theme.typography.h2,
    scrollMarginTop: 80,
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(3),
  },
  "& h3": {
    ...theme.typography.h5,
    scrollMarginTop: 80,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  "& h4": {
    fontSize: 26,
    fontWeight: 500,
    lineHeight: "32px",
    letterSpacing: "-0.03em",
    color: marketingPalette.inkMuted,
    marginTop: 0,
    marginBottom: 12,
  },
  // The two levels a few published bodies use beyond the editor's usual three:
  // an h1 opening three posts and h5 steps in one academy article, drawn in the
  // site's own h1 and h5.
  "& h1": {
    fontSize: 54,
    fontWeight: 600,
    lineHeight: "56px",
    letterSpacing: "-0.04em",
    color: marketingPalette.ink,
    marginTop: 0,
    marginBottom: 32,
    [NOT_WIDE]: { marginBottom: 24 },
    [MID]: { fontSize: 40, lineHeight: "48px", marginBottom: 16 },
    [TINY]: { fontSize: 34, lineHeight: "40px", marginBottom: 12 },
  },
  "& h5": {
    fontSize: 24,
    fontWeight: 700,
    lineHeight: "30px",
    letterSpacing: "-0.03em",
    color: marketingPalette.inkMuted,
    marginTop: 0,
    marginBottom: 12,
    [TINY]: { fontSize: 20 },
  },

  // The site's own mobile step, not MUI's `sm` (600px). The heading variants
  // carry their own steps; this one has no variant to carry it.
  [theme.breakpoints.down(SITE_STEPS.tiny)]: {
    "& h4": { fontSize: 18, lineHeight: "120%" },
  },

  // A figure is a full-width block whose inner wrapper shrink-wraps to the
  // image's intrinsic width; the wrapper is zeroed out so its whitespace does
  // not add a text line under the image.
  "& figure": {
    display: "block",
    width: "100%",
    maxWidth: "100%",
    margin: "0 auto 10px",
    textAlign: "center",
    position: "relative",
  },
  '& figure[data-align="center"]': {
    display: "table",
    maxWidth: "60%",
    textAlign: "start",
  },
  "& figure > div": {
    display: "inline-block",
    fontSize: 0,
    lineHeight: 0,
    color: "transparent",
  },
  '& figure[data-align="center"] > div': { maxWidth: "100%" },
  "& img": {
    maxWidth: "100%",
    height: "auto",
    display: "inline-block",
    verticalAlign: "middle",
    border: `1px solid ${marketingPalette.wash}`,
    borderRadius: 8,
  },
  // The wrapper's width, as Webflow sets it: the wrapper shrink-wraps the
  // picture, so a 480px screenshot stays 480 and a wide one takes the column,
  // at a width the height follows rather than one rounded back from it.
  "& figure img": { width: "100%", maxWidth: "100%" },
  "& figcaption": {
    fontSize: 16,
    lineHeight: "24px",
    color: "inherit",
    textAlign: "center",
    marginTop: 5,
  },
  '& figure[data-align="center"] > figcaption': {
    display: "table-caption",
    captionSide: "bottom",
  },
}));

export default ArticleBody;
